import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchCategories, fetchShowroomPhotos, productImageSrc } from "../lib/api";
import { ArrowUpRight, ChevronRight, ShieldCheck, Truck, Award, X } from "lucide-react";

export default function AboutPage() {
  const [categories, setCategories] = useState([]);
  const [showroomPhotos, setShowroomPhotos] = useState([]);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => setCategories([]));
    fetchShowroomPhotos().then(setShowroomPhotos).catch(() => setShowroomPhotos([]));
  }, []);

  return (
    <div className="bg-slate-50">
      {/* Heading + intro */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 lg:pt-32 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 ve-fade-up">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-600 mb-5">
              Veer Electrical · Since 1992
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight text-slate-900">
              Every wire,<br />
              every switch,<br />
              every spark of life — <span className="text-amber-500">curated.</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg text-slate-600 leading-relaxed max-w-xl">
              We've spent three decades curating the finest electrical products
              India makes — the brands you see in premium homes, the
              specifications engineers trust, and the quality that lasts a
              lifetime.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/boq"
                className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-full font-medium hover:bg-slate-800 transition-colors"
                data-testid="hero-cta-boq"
              >
                Request a quotation <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/category/switches"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-slate-900 border border-slate-200 rounded-full font-medium hover:bg-slate-100 transition-colors"
                data-testid="hero-cta-browse"
              >
                Browse showroom <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 lg:pl-8 lg:border-l lg:border-slate-200">
            <div className="grid grid-cols-3 gap-6">
              {[
                { n: "30+", l: "Years of trust" },
                { n: "40+", l: "Premium brands" },
                { n: "1000s", l: "Happy projects" },
              ].map((s) => (
                <div key={s.l}>
                  <p className="font-heading text-3xl lg:text-4xl font-semibold text-slate-900">
                    {s.n}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{s.l}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 text-sm text-slate-500 leading-relaxed">
              Authorised distributor partner for Schneider, Legrand, Panasonic,
              Havells, Polycab, Atomberg, Philips, Siemens, ABB and more.
            </p>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: ShieldCheck, title: "100% Authentic", desc: "Authorised dealer for every brand we sell." },
            { icon: Truck, title: "Project-grade supply", desc: "Bulk orders dispatched within 24 hours." },
            { icon: Award, title: "Certified expertise", desc: "Trained team to advise the right product." },
          ].map((f) => (
            <div key={f.title} className="flex items-start gap-4">
              <span className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                <f.icon className="w-5 h-5 text-amber-600" />
              </span>
              <div>
                <p className="font-heading font-semibold text-slate-900">{f.title}</p>
                <p className="text-sm text-slate-500 mt-0.5">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Showroom photos */}
      {showroomPhotos.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="mb-8 lg:mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-600 mb-3">
              Inside our showroom
            </p>
            <h2 className="font-heading text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 max-w-xl">
              Come and see every product,{" "}
              <span className="text-slate-400">up close.</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4">
            {showroomPhotos.map((p, i) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setLightboxIdx(i)}
                className="group relative aspect-square rounded-2xl overflow-hidden bg-slate-100 border border-slate-200 hover:border-amber-400 transition-all"
                data-testid={`showroom-photo-${p.id}`}
              >
                <img
                  src={productImageSrc(p.image_url)}
                  alt={p.caption || "Showroom"}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </section>
      )}

      {lightboxIdx !== null && showroomPhotos[lightboxIdx] && (
        <div
          className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxIdx(null)}
          data-testid="showroom-lightbox"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIdx(null);
            }}
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={productImageSrc(showroomPhotos[lightboxIdx].image_url)}
            alt=""
            className="max-w-full max-h-[90vh] object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-600 mb-3">
              The Showroom
            </p>
            <h2 className="font-heading text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 max-w-xl">
              Five categories. <span className="text-slate-400">Hundreds of choices.</span>
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {categories.map((c, i) => (
            <Link
              key={c.id}
              to={`/category/${c.id}`}
              className={`group relative overflow-hidden rounded-3xl bg-white border border-slate-200 hover:border-amber-400 transition-all hover:-translate-y-1 ${
                i === 0 ? "md:col-span-7 md:row-span-2" : "md:col-span-5"
              }`}
              data-testid={`home-category-${c.id}`}
            >
              <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                <img
                  src={productImageSrc(c.image)}
                  alt={c.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-6 lg:p-8">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-amber-600 font-bold mb-2">
                      {c.subtypes.length} categories
                    </p>
                    <h3 className="font-heading text-2xl font-semibold text-slate-900">
                      {c.name}
                    </h3>
                    <p className="mt-2 text-sm text-slate-500 max-w-md">{c.tagline}</p>
                  </div>
                  <span className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center group-hover:bg-amber-500 group-hover:text-slate-900 transition-colors flex-shrink-0">
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="relative rounded-3xl overflow-hidden bg-slate-900 text-white p-10 lg:p-16">
          <div className="ve-grain absolute inset-0 opacity-30" />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-amber-400 font-bold mb-3">
                Bill of quantities?
              </p>
              <h3 className="font-heading text-3xl lg:text-4xl font-semibold leading-tight">
                Send us your BOQ.<br />
                We'll quote it back the same day.
              </h3>
              <p className="mt-4 text-slate-300 max-w-lg">
                Upload a PDF or photograph of your requirement list. Our team
                replies with brand-wise pricing and availability — usually
                within hours.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Link
                to="/boq"
                className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-slate-900 rounded-full font-medium hover:bg-amber-400 transition-colors"
                data-testid="cta-boq"
              >
                Submit BOQ <ArrowUpRight className="w-4 h-4" />
              </Link>
              <a
                href="https://wa.me/917400042543"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 rounded-full font-medium hover:bg-white/20 transition-colors"
                data-testid="cta-whatsapp"
              >
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
